/**
 * UI preferences store (Zustand).
 * Stores user selections that persist across navigation:
 * - Which metrics to show in Screen B
 * - Pickup period selection
 * - Compare mode (vs LY, SDLY, Budget)
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";

type CompareMode = "ly" | "sdly" | "budget";
type PickupPeriod = 1 | 3 | 7 | 14 | 30;

interface UiState {
  // Screen B — metric selection
  visibleMetrics: string[];
  activePickupPeriod: PickupPeriod;
  compareMode: CompareMode;

  // Sidebar
  sidebarCollapsed: boolean;

  setVisibleMetrics: (metrics: string[]) => void;
  setActivePickupPeriod: (period: PickupPeriod) => void;
  setCompareMode: (mode: CompareMode) => void;
  toggleSidebar: () => void;
}

const DEFAULT_METRICS = [
  "occupancy_pct",
  "adr",
  "revpar",
  "pickup",
  "pace",
  "sdly_occupancy",
];

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      visibleMetrics: DEFAULT_METRICS,
      activePickupPeriod: 7,
      compareMode: "ly",
      sidebarCollapsed: false,

      setVisibleMetrics: (metrics) => set({ visibleMetrics: metrics }),
      setActivePickupPeriod: (period) => set({ activePickupPeriod: period }),
      setCompareMode: (mode) => set({ compareMode: mode }),
      toggleSidebar: () =>
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
    }),
    { name: "rms-ui" }
  )
);
